import type { TimeWindow } from '@provision/sidekick-utils';

export interface ScheduleWeekDayOption {
  /** the stored `weekDay`: 0 = Sunday … 6 = Saturday */
  value: number;
  label: string;
}

/** Monday first — the order the Business Hours settings page uses. */
export const SCHEDULE_WEEKDAY_OPTIONS: readonly ScheduleWeekDayOption[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5];
const MAX_NAMED_DAYS = 2;
const SHORT_LABEL_LENGTH = 3;

export const DEFAULT_SCHEDULE_TIME_FROM = '09:00';
export const DEFAULT_SCHEDULE_TIME_TO = '17:00';

/** A new schedule condition starts like a new escalation custom schedule: weekdays, 09:00–17:00. */
export function buildDefaultScheduleWindows(): TimeWindow[] {
  return expandScheduleDays(WEEKDAYS, DEFAULT_SCHEDULE_TIME_FROM, DEFAULT_SCHEDULE_TIME_TO);
}

/** One window per selected day in week order — the stored shape; every day shares the two times. */
export function expandScheduleDays(
  weekDays: readonly number[],
  timeFrom: string,
  timeTo: string,
): TimeWindow[] {
  return selectWeekDayOptions(weekDays).map((option) => ({
    weekDay: option.value,
    timeFrom,
    timeTo,
  }));
}

/** The day picker's collapsed text: named days up to two, "Weekdays" for Monday–Friday, a count past that. */
export function summarizeScheduleDays(weekDays: readonly number[]): string {
  const selected = selectWeekDayOptions(weekDays);

  if (selected.length === WEEKDAYS.length && WEEKDAYS.every((day) => weekDays.includes(day))) {
    return 'Weekdays';
  }

  if (selected.length > MAX_NAMED_DAYS) {
    return `${selected.length} selected`;
  }

  return selected.map((option) => option.label.slice(0, SHORT_LABEL_LENGTH)).join(', ');
}

/** The editor state behind stored windows: the distinct days and the times they share. */
export function describeScheduleWindows(windows: readonly TimeWindow[]): {
  weekDays: number[];
  timeFrom: string;
  timeTo: string;
} {
  return {
    weekDays: selectWeekDayOptions(windows.map((window) => window.weekDay)).map(
      (option) => option.value,
    ),
    timeFrom: windows[0]?.timeFrom ?? DEFAULT_SCHEDULE_TIME_FROM,
    timeTo: windows[0]?.timeTo ?? DEFAULT_SCHEDULE_TIME_TO,
  };
}

function selectWeekDayOptions(weekDays: readonly number[]): ScheduleWeekDayOption[] {
  return SCHEDULE_WEEKDAY_OPTIONS.filter((option) => weekDays.includes(option.value));
}
